import { EditorView } from "@codemirror/view";
import {
  autoUpdate,
  FloatingPortal,
  flip,
  offset,
  safePolygon,
  shift,
  useFloating,
  useHover,
  useInteractions,
} from "@floating-ui/react";
import { designVariables } from "@studio3t/design-variables";
import {
  GenericDropdown,
  type GenericDropdownItem,
  Icon,
} from "@studio3t/generic-components";
import { useQuery } from "@tanstack/react-query";
import CodeMirror, { type ReactCodeMirrorRef } from "@uiw/react-codemirror";
import {
  type CSSProperties,
  type MutableRefObject,
  type RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTheme } from "styled-components";
import { buildExtensions, CONTEXT_MENU_HEIGHT } from "@/components/JsonViewer";
import type { ExplorerItem } from "./explorerData";
import {
  fetchSampleDocument,
  fetchSchemaDefinition,
  fetchScriptContent,
  resourcePreviewQueryKey,
} from "./pipelineStaging";
import {
  HeaderActionSlot,
  PREVIEW_CARD_ATTR,
  PreviewCard,
  PreviewHeader,
  PreviewHeaderButton,
  PreviewScroll,
  PreviewTitle,
} from "./previewCardChrome";
import {
  previewsAreSuppressed,
  primaryButtonHeld,
  useContextMenuPin,
} from "./previewMenuPin";
import { rhaiHighlightStyle, rhaiLanguage } from "./rhaiLanguage";
import { SchemaDeleteModal, useSchemaDelete } from "./SchemaDelete";
import { parseSchemaRowId } from "./stagingModel";
import { TestPreviewHover } from "./TestPreview";

// The code hover-preview cards (Figma Bridge_DocPreview): the shared card
// shell lives in previewCardChrome.tsx; this module owns the CodeMirror
// content, the Copy menu, and the hover/floating wiring every preview kind
// (the test card included — TestPreview.tsx) rides on.
const PREVIEW_OPEN_DELAY_MS = 600;

// Same platform badge as the Raw configuration viewer's menu.
const IS_MAC =
  typeof navigator !== "undefined" &&
  (navigator.platform ?? "").toUpperCase().includes("MAC");
const KEY_COPY = IS_MAC ? "⌘C" : "Ctrl+C";

// The stored definition, pretty-printed when it parses (stored schemas may
// be minified); shown verbatim otherwise.
function prettyDefinition(definition: string | null | undefined) {
  if (!definition) return null;
  try {
    return JSON.stringify(JSON.parse(definition), null, 2);
  } catch {
    return definition;
  }
}

/** What a tree leaf's hover card shows. */
export type PreviewKind = "schema" | "script" | "sample" | "test";

/** The kinds whose card is the CodeMirror text preview below — "test"
 *  renders its own assertion-line card (TestPreview.tsx). */
type CodePreviewKind = Exclude<PreviewKind, "test">;

// One fetcher per previewable kind — the same loaders the old
// click-to-editor flow used.
const PREVIEW_FETCHERS: Record<
  CodePreviewKind,
  (
    resource: Parameters<typeof fetchSchemaDefinition>[0],
  ) => Promise<string | null>
> = {
  schema: fetchSchemaDefinition,
  script: fetchScriptContent,
  sample: fetchSampleDocument,
};

// The definition/sample fetch for one previewable resource — null with
// nothing (yet) to preview, e.g. between the row losing its item and the
// query going disabled.
function fetchPreviewResource(
  item: ExplorerItem | null,
  kind: CodePreviewKind | null,
): Promise<string | null> {
  if (!item || !kind) return Promise.resolve(null);
  return PREVIEW_FETCHERS[kind]({
    id: item.id,
    name: item.name,
    icon: item.icon,
    provider: item.provider,
  } as Parameters<typeof fetchSchemaDefinition>[0]);
}

function usePreviewContent(
  item: ExplorerItem | null,
  kind: CodePreviewKind | null,
) {
  return useQuery({
    // Shared with the editor's loadSample (editorContents) so a drop shows
    // the same generated sample this card cached.
    queryKey: resourcePreviewQueryKey(kind, item?.id),
    enabled: item !== null && kind !== null,
    // Same freshness window as the pipeline lint's reference lookups.
    staleTime: 15_000,
    queryFn: () => fetchPreviewResource(item, kind),
  });
}

// Copies the CodeMirror view's current selection to the clipboard; a no-op
// without a view or with nothing selected (the Copy item is disabled in
// that state, but a stray call — e.g. a stale ref — must still be safe).
function copySelectedText(view: EditorView | null | undefined) {
  if (!view) return;
  const sel = view.state.selection.main;
  if (sel.empty) return;
  navigator.clipboard
    .writeText(view.state.sliceDoc(sel.from, sel.to))
    .catch(() => {});
}

// The Raw configuration viewer's context menu on the preview: Copy for the
// current selection (disabled without one), Copy All for the whole
// definition.
function usePreviewContextMenu(
  cmRef: RefObject<ReactCodeMirrorRef | null>,
  content: string,
) {
  const [hasSelection, setHasSelection] = useState(false);
  const selectionListener = useMemo(
    () =>
      EditorView.updateListener.of((u) => {
        if (u.selectionSet) setHasSelection(!u.state.selection.main.empty);
      }),
    [],
  );

  const copySelection = useCallback(
    () => copySelectedText(cmRef.current?.view),
    [cmRef],
  );

  const copyAll = useCallback(() => {
    navigator.clipboard.writeText(content).catch(() => {});
  }, [content]);

  const items: GenericDropdownItem[] = useMemo(
    () => [
      [
        {
          id: "copy",
          type: "text",
          value: "copy",
          label: "Copy",
          badgeText: KEY_COPY,
          disabled: !hasSelection,
          closeOnSelect: true,
          onClick: copySelection,
        },
        {
          id: "copy-all",
          type: "text",
          value: "copy-all",
          label: "Copy All",
          closeOnSelect: true,
          onClick: copyAll,
        },
      ],
    ],
    [hasSelection, copySelection, copyAll],
  );

  return { items, selectionListener, copyAll };
}

// The floating preview card — rendered only while open, so the definition
// fetch starts with the card. Nothing shows until the content has landed;
// a failed fetch simply never shows a card. The card is a pointer target:
// entering it keeps it open (safePolygon on the hover), the code region
// scrolls on the app's OverlayScrollbars, and right-click opens the Raw
// configuration viewer's Copy / Copy All menu (onContextMenu pins the
// preview open for the menu's lifetime — the pointer moving onto the menu
// would otherwise hover-close the card under it).
function SchemaPreviewCard({
  item,
  kind,
  setFloating,
  style,
  floatingProps,
  onContextMenuOpen,
  onDelete,
}: {
  item: ExplorerItem;
  kind: CodePreviewKind;
  setFloating: (node: HTMLElement | null) => void;
  style: CSSProperties;
  floatingProps: Record<string, unknown>;
  onContextMenuOpen: () => void;
  /** Schema cards: the header's Delete, next to Copy. */
  onDelete?: () => void;
}) {
  const theme = useTheme();
  const cmRef = useRef<ReactCodeMirrorRef>(null);
  const cardRef = useRef<HTMLDivElement | null>(null);
  const { data } = usePreviewContent(item, kind);
  // Schemas may be stored minified — pretty-print them; scripts and
  // sample documents arrive display-ready.
  const content = useMemo(
    () => (kind === "schema" ? prettyDefinition(data) : (data ?? null)),
    [kind, data],
  );
  const menu = usePreviewContextMenu(cmRef, content ?? "");
  // Bare code per the design: no fold gutter, and long lines scroll behind
  // the card edge instead of wrapping. Scripts read as Rhai, the JSON
  // kinds keep the default JSON highlighting.
  const extensions = useMemo(
    () => [
      ...buildExtensions(theme, {
        lineWrapping: false,
        folding: false,
        ...(kind === "script"
          ? { language: rhaiLanguage, highlight: rhaiHighlightStyle }
          : {}),
      }),
      menu.selectionListener,
    ],
    [theme, kind, menu.selectionListener],
  );
  if (content === null) return null;
  return (
    <FloatingPortal>
      <PreviewCard
        ref={(node) => {
          cardRef.current = node;
          setFloating(node);
        }}
        style={style}
        data-testid="schema-preview-card"
        onContextMenu={onContextMenuOpen}
        {...floatingProps}
      >
        {/* Header copy button (hover state 14669-182478): visible only
            while the pointer is inside the card; copies the whole
            definition. */}
        <PreviewHeader>
          <PreviewTitle>PREVIEW</PreviewTitle>
          <HeaderActionSlot>
            <PreviewHeaderButton
              icon={Icon.Copy}
              label={`Copy ${item.name}`}
              onClick={menu.copyAll}
            />
            {onDelete && (
              <PreviewHeaderButton
                icon={Icon.Bin}
                label={`Delete ${item.name}`}
                onClick={onDelete}
              />
            )}
          </HeaderActionSlot>
        </PreviewHeader>
        <PreviewScroll>
          <CodeMirror
            ref={cmRef}
            value={content}
            extensions={extensions}
            readOnly
            editable={false}
            basicSetup={{
              // The design shows bare code — no gutters (14662-144779).
              lineNumbers: false,
              foldGutter: false,
              highlightActiveLine: false,
              highlightActiveLineGutter: false,
            }}
          />
        </PreviewScroll>
        <GenericDropdown
          asContextMenu={cardRef}
          dropdownItems={menu.items}
          width={{ strategy: "fixed", size: 200 }}
          height={CONTEXT_MENU_HEIGHT}
          // The card floats at z-index 1000 — the menu must beat it or it
          // opens underneath.
          floatingZIndex={1001}
        />
      </PreviewCard>
    </FloatingPortal>
  );
}

/** The hover-preview unit for a code leaf: the card while open and, for
 *  schema rows, the delete-confirmation modal mounted OUTSIDE the card so
 *  it survives the card hover-closing under its backdrop (the suite card's
 *  arrangement, TestPreviewHover). */
function CodePreviewHover({
  item,
  kind,
  open,
  setFloating,
  style,
  floatingProps,
  onContextMenuOpen,
  closeCard,
}: {
  item: ExplorerItem;
  kind: CodePreviewKind;
  open: boolean;
  setFloating: (node: HTMLElement | null) => void;
  style: CSSProperties;
  floatingProps: Record<string, unknown>;
  onContextMenuOpen: () => void;
  closeCard: () => void;
}) {
  const schema = kind === "schema" ? parseSchemaRowId(item.id) : null;
  const flow = useSchemaDelete(schema);
  return (
    <>
      {open && (
        <SchemaPreviewCard
          item={item}
          kind={kind}
          setFloating={setFloating}
          style={style}
          floatingProps={floatingProps}
          onContextMenuOpen={onContextMenuOpen}
          onDelete={
            schema
              ? () => {
                  closeCard();
                  flow.request();
                }
              : undefined
          }
        />
      )}
      {schema && <SchemaDeleteModal name={item.name} flow={flow} />}
    </>
  );
}

// Claim the single-preview baton while open: closing the previous holder
// (even a pinned one — its menu unmounts with its card, and its watcher
// release becomes a no-op) makes room for this preview.
function useSinglePreviewBaton(
  open: boolean,
  menuPinnedRef: MutableRefObject<boolean>,
  setOpen: (open: boolean) => void,
) {
  useEffect(() => {
    if (!open) return;
    closeActivePreview?.();
    const closeThis = () => {
      menuPinnedRef.current = false;
      setOpen(false);
    };
    closeActivePreview = closeThis;
    return () => {
      if (closeActivePreview === closeThis) closeActivePreview = null;
    };
  }, [open, menuPinnedRef, setOpen]);
}

// Only one preview may be up at a time, across all rows: opening a new one
// closes the previous — including a context-menu-pinned one, so a pinned
// card never blocks (or stacks under) the next row's preview. Module-level
// baton between the hook's instances.
let closeActivePreview: (() => void) | null = null;

/** Closes whichever row's preview is up (no-op when none) — a drag start
 *  calls this so no card floats over the drop zones, whatever row it
 *  belonged to (useCloseOnDrag only knows the dragged row's own). */
export function closeOpenPreview() {
  closeActivePreview?.();
}

// Previews pause while the tree scrolls: rows sliding under a resting
// pointer must not pop cards, and an open card scrolls away from its row.
// Module-level like the baton — the row hooks consult the flag at open
// time (onOpenChange), so scrolling never re-renders the tree.
let treeScrolling = false;
const SCROLL_IDLE_MS = 200;

// Capture-phase scroll handler for the explorer panel (scroll doesn't
// bubble; React's onScrollCapture still sees descendants' events): flags
// scrolling, closes the active card, and re-arms previews after a quiet
// SCROLL_IDLE_MS. A card then returns only via a fresh hover. The card
// itself scrolls too, and — portaled or not — it is a React child of the
// panel, so its scroll events land here as well: those must NOT close it.
export function usePreviewScrollGuard(): (e: React.UIEvent) => void {
  const idleTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (idleTimer.current) clearTimeout(idleTimer.current);
      treeScrolling = false;
    },
    [],
  );
  return useCallback((e: React.UIEvent) => {
    // Scrolling inside the floating card keeps it open — only the tree's
    // own scrolling slides rows under the pointer.
    if (
      e.target instanceof Element &&
      e.target.closest(`[${PREVIEW_CARD_ATTR}]`)
    )
      return;
    treeScrolling = true;
    closeActivePreview?.();
    if (idleTimer.current) clearTimeout(idleTimer.current);
    idleTimer.current = setTimeout(() => {
      treeScrolling = false;
      idleTimer.current = null;
    }, SCROLL_IDLE_MS);
  }, []);
}

// A scrolling tree (or an open suite-delete confirm, see previewMenuPin's
// suppression flag) suppresses opening; a context-menu pin suppresses
// closing until the menu releases it.
function acceptsOpenChange(next: boolean, menuPinned: boolean) {
  if (next) {
    return !treeScrolling && !previewsAreSuppressed() && !primaryButtonHeld();
  }
  return !menuPinned;
}

// Floating-ui wiring for one row: the card's placement, the long-hover
// trigger, and the reference element. The row ref is owned by the drag
// hook — adopt its element on every render instead of merging callback
// refs.
function usePreviewFloating({
  open,
  setOpen,
  menuPinnedRef,
  rowRef,
  hoverEnabled,
}: {
  open: boolean;
  setOpen: (open: boolean) => void;
  menuPinnedRef: MutableRefObject<boolean>;
  rowRef: RefObject<HTMLDivElement | null>;
  hoverEnabled: boolean;
}) {
  const { refs, floatingStyles, context } = useFloating({
    open,
    onOpenChange: (next) => {
      if (acceptsOpenChange(next, menuPinnedRef.current)) setOpen(next);
    },
    placement: "right-start",
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(designVariables.ui.semantic.spacings),
      flip(),
      shift({ padding: designVariables.ui.semantic.spacings }),
    ],
  });
  const hover = useHover(context, {
    enabled: hoverEnabled,
    delay: { open: PREVIEW_OPEN_DELAY_MS, close: 0 },
    // Keeps the preview open while the pointer travels from the row into
    // the card (and while it stays there), so the code can be scrolled.
    handleClose: safePolygon(),
  });
  const { getReferenceProps, getFloatingProps } = useInteractions([hover]);

  useEffect(() => {
    refs.setReference(rowRef.current);
  });

  return { refs, floatingStyles, getReferenceProps, getFloatingProps };
}

// A drag that starts mid-hover keeps the pointer down over the row —
// close the preview instead of leaving it floating over the drop zones.
function useCloseOnDrag(dragging: boolean, setOpen: (open: boolean) => void) {
  // biome-ignore lint/correctness/useExhaustiveDependencies: the drag flag is the only trigger; setOpen is the row's stable useState setter
  useEffect(() => {
    if (dragging) setOpen(false);
  }, [dragging]);
}

// Hover-preview wiring for a previewable tree leaf (schema, script or a
// collection's sample document). The caller owns the row element (it
// doubles as the drag handle), so the reference is adopted from that ref;
// spread the returned props onto the row via getReferenceProps.
export function useResourceHoverPreview(
  item: ExplorerItem | null,
  kind: PreviewKind | null,
  rowRef: RefObject<HTMLDivElement | null>,
  dragging: boolean,
) {
  const [open, setOpen] = useState(false);
  const { menuPinnedRef, pinForContextMenu } = useContextMenuPin(
    rowRef,
    setOpen,
  );
  const previewable = item !== null && kind !== null;
  const { refs, floatingStyles, getReferenceProps, getFloatingProps } =
    usePreviewFloating({
      open,
      setOpen,
      menuPinnedRef,
      rowRef,
      hoverEnabled: previewable && !dragging,
    });

  useSinglePreviewBaton(open, menuPinnedRef, setOpen);
  useCloseOnDrag(dragging, setOpen);

  // The hover units mount even while closed: their delete-confirmation
  // modals (suite and schema rows) must survive the card hover-closing
  // under the modal backdrop.
  const close = useCallback(() => setOpen(false), []);
  const preview = !previewable ? null : kind === "test" ? (
    <TestPreviewHover
      item={item}
      open={open}
      setFloating={refs.setFloating}
      style={floatingStyles}
      floatingProps={getFloatingProps()}
      closeCard={close}
    />
  ) : (
    <CodePreviewHover
      item={item}
      kind={kind}
      open={open}
      setFloating={refs.setFloating}
      style={floatingStyles}
      floatingProps={getFloatingProps()}
      onContextMenuOpen={pinForContextMenu}
      closeCard={close}
    />
  );

  // For row-level context menus (the suite and schema rows' Delete): an
  // open card must not sit over the menu, so the row closes it on
  // contextmenu.

  return { getReferenceProps, preview, close };
}
